<script setup>
import { ref, computed } from 'vue';
import { fullName, initials } from '../domain/format.js';
import { useStoredImage } from '../composables/useStoredImage.js';
import { useFileActions } from '../composables/useFileActions.js';
import { useConfirm } from '../composables/useConfirm.js';
import { useAuth } from '../composables/useAuth.js';
import { replacePlayerPhoto } from '../domain/playerPhoto.js';
import { variantForSize } from '../domain/photoVariants.js';
import { storeVariants } from '../composables/usePhotoVariants.js';
import { trackUpload } from '../composables/useUploadTracker.js';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  player: { type: Object, required: true },
  /* Bedeutet die Höhe der Box. Die Breite ergibt sich im Verhältnis 3:4 —
     dasselbe Seitenverhältnis wie der große Bildplatz in der Spielerakte
     (.info-photo), an den echten Fotos nachgemessen (Ø 0,71 über alle Fotos
     in AFH/GFC/AvatarPics, Bastian-Entscheidung 2026-09-29: 3:4 statt 2:3). */
  size: { type: Number, default: 40 },
  /* Nur im Panel-Kopf: dort lässt sich das Foto durch Anklicken wechseln.
     In der Liste wäre das ein Fehlklick-Risiko — dort öffnet ein Klick die Akte. */
  editable: { type: Boolean, default: false },
  /* Anzuzeigendes Bild statt der Vorgabe (avatar / avatarPath) — für den großen
     Bildplatz in Player Info, der ein hinterlegtes bioPhoto bevorzugt. Der
     Upload ist derselbe und setzt weiterhin avatarPath. */
  displaySrc: { type: String, default: null },
  /* Großer Bildplatz (Player Info): größere Initialen, weichere Ecken. */
  large: { type: Boolean, default: false },
});

const width = computed(() => Math.round(props.size * 3 / 4));

const { t } = useI18n();
const { can } = useAuth();
const { upload, busy: uploading } = useFileActions();
/* Ganzer Vorgang inkl. verkleinerter Fassungen, nicht nur der Upload des Originals. */
const working = ref(false);
const busy = computed(() => uploading.value || working.value);
const { confirm } = useConfirm();
const input = ref(null);

/* Zwei Quellen: `avatar` hält das Bild direkt (Altbestand als Data-URL),
   `avatarPath` verweist auf eine Datei im geschützten Speicher. Angezeigt wird
   die verkleinerte Fassung passend zur Boxgröße (Liste: thumb, große Karte: md),
   sonst das Original (domain/photoVariants.js). Mit displaySrc wird nichts
   selbst geladen. */
const { url: storedUrl } = useStoredImage(
  () => (props.displaySrc ? null : props.player.avatarPath),
  () => variantForSize(props.size)
);

const src = computed(() => props.displaySrc || props.player.avatar || storedUrl.value);
const label = computed(() => initials(fullName(props.player)));
const canEdit = computed(() => props.editable && can('players', 'write'));

async function onPick(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  const previous = props.player.avatarPath;
  if (previous) {
    const ok = await confirm({
      title: t('confirm.replacePhotoTitle'),
      message: t('confirm.replacePhoto'),
      confirmLabel: t('files.replace'),
    });
    if (!ok) return;
  }

  /* Setzt avatarPath und leert avatar, bioPhoto und bioPhotoPath, damit das neue
     Foto überall sichtbar ist (domain/playerPhoto.js). Die verkleinerten
     Fassungen entstehen im Anschluss an den Upload des Originals; scheitert das,
     steht das Original trotzdem, und die Anzeige nimmt es.

     Das vorherige Foto (Original und Fassungen) wird hier NICHT gelöscht: Die
     Datenbank zeigt bis zum Speichern-Knopf noch darauf, und Verwerfen muss den
     alten Stand zurückbringen. Aufgeräumt wird erst nach erfolgreichem Speichern
     (PlayerPanel.onSave, domain/fileCleanup.js). */
  working.value = true;
  try {
    const stored = await replacePlayerPhoto(props.player, file, async (f, folder) => {
      const stored = await upload(f, folder);
      try {
        await storeVariants(f, stored.path);
      } catch (e) {
        console.error('[photos] Verkleinerte Fassungen fehlgeschlagen:', e && e.message);
      }
      return stored;
    });
    /* Für das Aufräumen nach dem Speichern merken (domain/fileCleanup.js) */
    trackUpload(props.player.id, 'photo', stored.path);
  } finally {
    working.value = false;
  }
}
</script>

<template>
  <component
    :is="canEdit ? 'button' : 'div'"
    class="avatar-box portrait"
    :class="{ editable: canEdit, busy, xl: large, 'has-photo': !!src }"
    :style="{ width: width + 'px', height: size + 'px' }"
    :title="canEdit ? $t('player.changePhoto') : undefined"
    @click="canEdit && input.click()"
  >
    <img v-if="src" :src="src" alt="" />
    <span v-else class="avatar-fallback">{{ label }}</span>
    <span v-if="canEdit" class="edit-badge" aria-hidden="true">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
        <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16z" stroke-linejoin="round" />
      </svg>
    </span>

    <input
      v-if="canEdit"
      ref="input"
      type="file"
      accept="image/*"
      hidden
      @click.stop
      @change="onPick"
    />
  </component>
</template>

<style scoped>
.avatar-box.editable {
  position: relative;
  padding: 0;
  border: 1px solid var(--border);
  cursor: pointer;
}
.avatar-box.busy {
  opacity: 0.6;
  cursor: default;
}
.edit-badge {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--card);
  border: 1px solid var(--border);
  color: var(--muted);
}
.avatar-box.editable:hover .edit-badge {
  color: var(--green);
  border-color: var(--green-dim);
}
</style>
